/**
 * V5 详细报告 raw → view model。
 *
 * 在粗略 view model（adapters/reportV5Adapter.js）之上叠加 detailed 子块：
 *   detailed.ability_deep_dive  → vm.detailed.abilities (按 ability 顺序，含分组)
 *   detailed.trait_deep_dive    → vm.detailed.traits   (按 TRAIT_CODES 顺序)
 *   detailed.growth_path        → vm.detailed.growth_path (phases code → {code,name})
 *
 * Schema 提议见 docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 frontend 条目。
 * 后端最终 schema 可能微调（命名 / 长度），本 adapter 保持 lenient：缺字段不抛异常。
 */
import { toReportV5ViewModel } from './reportV5Adapter';
import {
  ABILITY_LABELS,
  ABILITY_ORDER,
  LAYER_LABELS,
  LAYER_ORDER,
  TRAIT_LABELS,
  TRAIT_ORDER,
} from '../constants/reportV5Labels';

function abilityName(code) {
  return ABILITY_LABELS[code]?.name || code;
}
function layerName(code) {
  return LAYER_LABELS[code]?.name || code;
}

export function toReportV5DetailedViewModel(body) {
  // 先拿粗略 VM 作为基底（含 meta / overall / abilities / traits / scenarios / 等）
  const base = toReportV5ViewModel(body);

  // 提取 detailed 块
  const inner = body?.user_report_v5 && typeof body.user_report_v5 === 'object'
    ? body.user_report_v5
    : body;
  const d = inner?.detailed && typeof inner.detailed === 'object' ? inner.detailed : null;

  if (!d) {
    // 未解锁 / 后端没下发 detailed —— 返回基底 VM + 标记
    return { ...base, is_detailed: false, detailed: null };
  }

  // --- ability_deep_dive: 8 项按 ABILITY_ORDER 排，合并粗略 abilities 的分数/层数据 ---
  const abilityVMByCode = new Map(base.abilities.map((a) => [a.code, a]));
  const deepByCode = d.ability_deep_dive || {};
  const detailedAbilities = ABILITY_ORDER.map((code) => {
    const baseA = abilityVMByCode.get(code) || {};
    const deepA = deepByCode[code] || {};
    return {
      code,
      name: baseA.name || abilityName(code),
      layer: baseA.layer || ABILITY_LABELS[code]?.layer || '',
      layer_sort: baseA.layer_sort ?? ABILITY_LABELS[code]?.layer_sort ?? 0,
      percentage: baseA.percentage ?? 0,
      l_grade: baseA.l_grade || '',
      l_grade_label: baseA.l_grade_label || '',
      current_state_summary: deepA.current_state_summary || '',
      impact_and_direction: deepA.impact_and_direction || '',
    };
  });
  const abilitiesDeepByLayer = LAYER_ORDER.map((layerCode) => ({
    layer: layerCode,
    layer_name: layerName(layerCode),
    abilities: detailedAbilities
      .filter((a) => a.layer === layerCode)
      .sort((x, y) => (x.layer_sort || 0) - (y.layer_sort || 0)),
  }));

  // --- trait_deep_dive: 3 项按 TRAIT_ORDER 排，合并基础 traits VM ---
  const traitVMByCode = new Map(base.traits.map((t) => [t.code, t]));
  const deepTraitByCode = new Map(
    (Array.isArray(d.trait_deep_dive) ? d.trait_deep_dive : []).map((t) => [t?.code, t]),
  );
  const detailedTraits = TRAIT_ORDER.map((code) => {
    const baseT = traitVMByCode.get(code) || {};
    const deepT = deepTraitByCode.get(code) || {};
    return {
      code,
      name: baseT.name || TRAIT_LABELS[code]?.name || code,
      tier: baseT.tier || '',
      icon: baseT.icon || TRAIT_LABELS[code]?.icon || 'circle',
      narrative: baseT.narrative || '',  // 粗略报告的 50-80 字 narrative
      behavior: deepT.behavior || '',
      ai_impact: deepT.ai_impact || '',
      suggested_action: deepT.suggested_action || '',
    };
  });

  // --- growth_path: phases 里 focus_ability_codes 解析成 {code, name} ---
  const rawGrowth = d.growth_path || {};
  const rawPhases = Array.isArray(rawGrowth.phases) ? rawGrowth.phases : [];
  const phases = rawPhases.slice(0, 3).map((p, idx) => ({
    phase_index: Number(p?.phase_index) || idx + 1,
    phase_label: p?.phase_label || `阶段 ${idx + 1}`,
    phase_title: p?.phase_title || '',
    phase_goal: p?.phase_goal || '',
    learning_items: Array.isArray(p?.learning_items) ? p.learning_items.slice(0, 6) : [],
    focus_abilities: (Array.isArray(p?.focus_ability_codes) ? p.focus_ability_codes : [])
      .slice(0, 3)
      .map((code) => ({ code, name: abilityName(code) })),
  }));

  return {
    ...base,
    is_detailed: true,
    detailed: {
      abilities: detailedAbilities,
      abilities_by_layer: abilitiesDeepByLayer,
      traits: detailedTraits,
      trait_synthesis: d.trait_synthesis || '',
      collaboration_style: d.collaboration_style || '',
      not_recommended_tasks: Array.isArray(d.not_recommended_tasks)
        ? d.not_recommended_tasks.slice(0, 5)
        : [],
      growth_path: {
        overall_goal: rawGrowth.overall_goal || '',
        phases,
        follow_up_suggestions: Array.isArray(rawGrowth.follow_up_suggestions)
          ? rawGrowth.follow_up_suggestions.slice(0, 5)
          : [],
        application_tips: rawGrowth.application_tips || '',
      },
    },
  };
}
